import React, { memo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ISelectOption, Select } from 'components/forms/Selects'
import { IOption } from 'components/forms/combobox/types'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { IStore } from 'apps/fleet-planner/utils/types/store'

export interface IDispatcherSelectProps {
  /**
   * @default false
   */
  large?: boolean
  /**
   * @default true
   */
  fetchList?: boolean
  /**
   * @default false
   */
  required?: boolean
  /**
   * @default false
   */
  invalid?: boolean
  /**
   * @default 'Dispatcher'
   */
  label?: string
  /**
   * @default 'Select a Dispatcher'
   */
  placeholder?: string
  dispatcherId?: string
  validationMessage?: string
  onChange: (dispatcherId?: string) => void
}

interface IDispatchersListProps {
  dispatchers: IOption[]
}

/** @deprecated consider using DispatcherAutosuggest instead */
export const DispatcherSelect = memo<IDispatcherSelectProps>(
  ({
    dispatcherId,
    fetchList = true,
    invalid = false,
    label: labelProp = 'Dispatcher',
    large = false,
    onChange,
    placeholder = 'Select a Dispatcher',
    required = false,
    validationMessage,
  }) => {
    const dispatch = useDispatch()
    const { dispatchers } = useSelector<IStore, IDispatchersListProps>(
      ({ dispatchers: dispatchersStore }) => ({
        dispatchers: dispatchersStore.dispatchersAutocomplete,
      })
    )

    /**
     * Dispatch fetch on mount, only once
     */
    useEffect(() => {
      if (fetchList) {
        dispatch(fetchDispatchers())
      }
    }, [fetchList, dispatch])

    /**
     * Convert dispatchers list to ISelectOption<number>[]
     */
    const dispatcherOptions: ISelectOption<
      string
    >[] = dispatchers.map(({ id: value = '', text: label = '' }) => ({ label, value }))

    const dispatcherHandler = useCallback(
      (option: ISelectOption<string>) => onChange(option?.value),
      [onChange]
    )

    return (
      <Select
        label={labelProp}
        placeholder={placeholder}
        options={dispatcherOptions}
        value={`${dispatcherId}`}
        onChange={dispatcherHandler}
        large={large}
        required={required}
        validationMessage={validationMessage}
        invalid={invalid}
        dataTest='dispatcher-select'
      />
    )
  }
)
